import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { Dispatch } from 'redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { NotificationContainer } from 'components-stateless/Notifications';
import { ReduxNotificationHandler } from 'containers/Notifications/ReduxNotificationHandler';
import { ZustandNotificationHandler } from 'containers/Notifications/ZustandNotificationHandler';
import { fetchWhiteLabelling as fetchWhiteLabellingAction } from 'containers/WhiteLabelling/_utils/actions';
import {
  selectWhiteLabelling,
  selectIsWhiteLabellingConfigured,
} from 'containers/WhiteLabelling/_utils/selectors';
import { defaultWhiteLabelling } from 'components-stateless/constants';
import type { IWhiteLabellingConfig } from 'containers/WhiteLabelling/_utils/types';
import { WHITE_LABELLING_SOURCES } from 'constants/appConstants';
import AllThemeProvider from 'containers/Theme/AllThemeProvider';
import { getExtensionFromFileName } from 'utils/files';
import { withRouter } from 'utils/routing/withRouter';
import { MuiDateProvider } from 'components-stateless/MuiDateProvider';
import { SuspenseOutlet } from 'utils/routing/SuspenseOutlet';
import { DisneyGeoFooter } from 'components-stateless/DisneyGeoFooter';
import GlobalStyle from '../../styles/global-styles';

interface Props {
  whiteLabelling: IWhiteLabellingConfig;
  fetchWhiteLabelling: (
    whiteLabellingSource: string,
    value: string | number
  ) => void;
  params: {
    pubAssessmentUuid?: string;
    pubTeamUuid?: string;
  };
  isWhiteLabellingConfigured: boolean;
}

// eslint-disable-line react/prefer-stateless-function
class App extends React.PureComponent<Props> {
  componentDidMount() {
    const { params, fetchWhiteLabelling } = this.props;
    const domain = window.location.hostname;

    // eslint-disable-next-line max-len
    // here are three values for public access which can be used to fetch a white labelling config now.
    // whiteLabellingSource denotes what value we are using to query for configs.

    // The domain is used prior to login (for the login page)
    let whiteLabellingSource = WHITE_LABELLING_SOURCES.DOMAIN;
    let value: string | number = domain;

    // The public team uuid is used for a public assessment landing page
    if (params.pubTeamUuid) {
      whiteLabellingSource = WHITE_LABELLING_SOURCES.PUB_TEAM_UUID;
      value = params.pubTeamUuid;
    }

    // The public assessment uuid is used for a public assessments during execution
    if (params.pubAssessmentUuid) {
      whiteLabellingSource = WHITE_LABELLING_SOURCES.PUB_ASSESSMENT_UUID;
      value = params.pubAssessmentUuid;
    }

    fetchWhiteLabelling(whiteLabellingSource, value);
  }

  getFaviconType = (faviconURL: string): string | undefined => {
    const iconExtension = getExtensionFromFileName(faviconURL);
    switch (iconExtension) {
      case 'ico':
        return 'image/x-icon';
      case 'png':
        return 'image/png';
      default:
        return undefined;
    }
  };

  render() {
    const { whiteLabelling, isWhiteLabellingConfigured } = this.props;
    const whiteLabellingConfig = isWhiteLabellingConfigured
      ? whiteLabelling
      : defaultWhiteLabelling;

    const { faviconURL } = whiteLabellingConfig;
    const faviconType = this.getFaviconType(faviconURL);

    return (
      <AllThemeProvider allowWhitelabelling>
        <MuiDateProvider>
          <Helmet titleTemplate="%s - Checkbox" defaultTitle="Checkbox">
            {faviconURL && (
              <link rel="icon" type={faviconType} href={faviconURL} />
            )}
          </Helmet>
          <SuspenseOutlet />
          <NotificationContainer />
          <ReduxNotificationHandler />
          <ZustandNotificationHandler />
          <DisneyGeoFooter />
          <GlobalStyle />
        </MuiDateProvider>
      </AllThemeProvider>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  whiteLabelling: selectWhiteLabelling(),
  isWhiteLabellingConfigured: selectIsWhiteLabellingConfigured(),
});

const mapDispatchToProps = (dispatch: Dispatch<IAction>) => ({
  dispatch,
  fetchWhiteLabelling: (whiteLabellingSource: string, value: string | number) =>
    dispatch(fetchWhiteLabellingAction(whiteLabellingSource, value)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
